import React, { useCallback, useRef, useState } from 'react';
import _get from 'lodash/get';
import styles from './index.module.scss';

import { If } from 'utils';
import { UiKit } from 'components';
import { Tiles, Form } from '../.';
import { useMutation, useStoreConnect } from 'utils/hooks';
import { getEcomPlatformMap } from 'utils/DICTIONARY';
import { getSafeRedirectUrl } from 'utils/methods';
import { STEPS } from './stubs';

export default function StoreConnectModal() {
  const { isOpen, onClose } = useStoreConnect();

  const [step, setStep] = useState(STEPS.ECOMM_PLATFORM);
  /** @type {[import('utils/types').EcommPlatformSource | null, React.Dispatch<React.SetStateAction<import('utils/types').EcommPlatformSource | null>>]} */
  const [ecommPlatform, setEcommPlatform] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const labelMap = useRef(getEcomPlatformMap()).current;

  const title =
    step === STEPS.ECOMM_PLATFORM
      ? 'Connect New Store'
      : `Connect ${_get(labelMap, ecommPlatform)}`;

  const { mutate } = useMutation('seller.stores.post', 'POST', {
    errorMessage: 'Unable to connect the store. Please try again later.',
    payload: () => ({
      ecommerce_platform: ecommPlatform,
      redirect_url: '/account-settings'
    }),
    onSuccess: data => {
      const redirectUrl = getSafeRedirectUrl(_get(data, 'ecom_redirect_url'));
      if (!redirectUrl) throw new Error('Invalid store redirect URL');
      window.location.assign(redirectUrl);
    },
    onError: () => setIsConnecting(false)
  });

  const onStoreSelect = useCallback(id => {
    setEcommPlatform(id);
    setStep(STEPS.STORE_CONNECT);
  }, []);

  const onGotoStoreSelect = useCallback(() => {
    setEcommPlatform(null);
    setStep(STEPS.ECOMM_PLATFORM);
  }, []);

  const onStoreConnect = useCallback(
    (_, data) => {
      setIsConnecting(true);
      mutate({ store: _get(data, 'shop', '').trim() });
    },
    [mutate]
  );

  const onClosed = useCallback(() => setStep(STEPS.ECOMM_PLATFORM), []);

  return (
    <UiKit.Modal isOpen={isOpen} onClose={onClose} onClosed={onClosed}>
      <UiKit.Modal.Header title={title} onClose={onClose} />
      <UiKit.Modal.Body>
        <If test={step === STEPS.ECOMM_PLATFORM}>
          <Tiles onClick={onStoreSelect} />
        </If>
        <If test={step === STEPS.STORE_CONNECT}>
          <Form
            store={ecommPlatform}
            className={styles.content}
            isConnecting={isConnecting}
            onConnect={onStoreConnect}
            onCancel={onGotoStoreSelect}
          />
        </If>
      </UiKit.Modal.Body>
    </UiKit.Modal>
  );
}
